import React, { useState } from 'react';
import { XIcon, PlusIcon, TrashIcon } from './icons/AdminIcons';
import './PipelinePropertiesModal.css';

export interface PipelineFieldDef {
  key: string;
  label: string;
}

interface PipelinePropertiesModalProps {
  isOpen: boolean;
  onClose: () => void;
  pipelineName: string;
  pipelineId: number | null;
  fieldSchema: PipelineFieldDef[];
  onSave: (fieldSchema: PipelineFieldDef[]) => Promise<void>;
}

function slugFromLabel(label: string): string {
  return label
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

const PipelinePropertiesModal: React.FC<PipelinePropertiesModalProps> = ({
  isOpen,
  onClose,
  pipelineName,
  pipelineId,
  fieldSchema,
  onSave
}) => {
  const [localFields, setLocalFields] = useState<PipelineFieldDef[]>(fieldSchema);
  const [newLabel, setNewLabel] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setLocalFields([...fieldSchema]);
      setNewLabel('');
    }
  }, [isOpen, fieldSchema]);

  const handleAdd = () => {
    const label = newLabel.trim();
    if (!label) {
      alert('El nombre del campo es requerido');
      return;
    }
    let key = slugFromLabel(label);
    if (!key) key = 'campo';
    let baseKey = key;
    let n = 1;
    while (localFields.some(f => f.key === key)) {
      key = `${baseKey}_${n}`;
      n++;
    }
    setLocalFields([...localFields, { key, label }]);
    setNewLabel('');
  };

  const handleRemove = (index: number) => {
    setLocalFields(localFields.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (pipelineId == null) return;
    setIsSaving(true);
    try {
      await onSave(localFields);
      onClose();
    } catch (e) {
      alert('Error al guardar las propiedades');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="pipeline-properties-modal-overlay" onClick={onClose}>
      <div className="pipeline-properties-modal" onClick={e => e.stopPropagation()}>
        <div className="pipeline-properties-modal-header">
          <h2>Gestionar propiedades</h2>
          <button type="button" className="pipeline-properties-modal-close" onClick={onClose}>
            <XIcon width={20} height={20} />
          </button>
        </div>
        <div className="pipeline-properties-modal-body">
          <p className="pipeline-properties-subtitle">
            Campos particulares del pipeline <strong>{pipelineName}</strong>. Agregá columnas por nombre; la clave se genera automáticamente.
          </p>
          <div className="pipeline-properties-list">
            {localFields.map((field, index) => (
              <div key={field.key} className="pipeline-properties-row">
                <span className="pipeline-properties-label">{(field.label || field.key).replace(/\s*\(actualizado\)\s*/gi, '').trim() || field.key}</span>
                <button
                  type="button"
                  className="pipeline-properties-remove"
                  onClick={() => handleRemove(index)}
                  title="Eliminar"
                >
                  <TrashIcon width={16} height={16} />
                </button>
              </div>
            ))}
          </div>
          <div className="pipeline-properties-add">
            <input
              type="text"
              className="pipeline-properties-input"
              placeholder="Nombre del campo (ej: Participación en programa)"
              value={newLabel}
              onChange={e => setNewLabel(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
            />
            <button type="button" className="pipeline-properties-add-btn" onClick={handleAdd}>
              <PlusIcon width={16} height={16} /> Agregar
            </button>
          </div>
        </div>
        <div className="pipeline-properties-modal-footer">
          <button type="button" className="pipeline-properties-cancel" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="pipeline-properties-save" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PipelinePropertiesModal;
